// Reports — the centralized reports manager (tab guarded by `reports.view`).
//   /reports                → catalog, first report selected
//   /reports/:reportKey     → that report's workbench
//   /reports?tab=schedules|history|notifications
// Every report runs in the caller's own data scope unless they hold
// `reports_all.view` (super admins always do).
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BarChart3, CalendarClock, History, BellRing, Globe, Lock, Loader2, FileText, Star, Sparkles, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePageBreadcrumb } from "@/context/BreadcrumbContext";
import { useAuth } from "@/context/AuthContext";
import { ReportWorkbench } from "./reports/ReportWorkbench";
import { SchedulesTab } from "./reports/SchedulesTab";
import { RunsTab } from "./reports/RunsTab";
import { NotificationPrefsTab } from "./reports/NotificationPrefsTab";
import { AiSpendPanel } from "@/components/AiSpendPanel";
import { CdrFilesTab } from "./reports/cdr/CdrFilesTab";

const TABS = ["catalog", "schedules", "history", "notifications", "cdr-files", "ai-spend"];

function CatalogItem({ r, active, pinned, onOpen, onToggleFav, testid }) {
  return (
    <li className="group relative">
      <button type="button" onClick={() => onOpen(r.key)} data-testid={testid}
              className={`w-full rounded-lg border-l-2 py-2 pl-3 pr-9 text-left text-sm transition-colors ${
                active ? "border-primary bg-[#eef2fb] font-semibold text-primary dark:bg-slate-800"
                       : "border-transparent text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/60"}`}>
        <span className="block truncate">{r.label}</span>
        <span className="mt-0.5 block text-[11px] font-normal text-slate-400">{r.formats.map((f) => f.toUpperCase()).join(" · ")}</span>
      </button>
      <button type="button" onClick={(e) => { e.stopPropagation(); onToggleFav(r.key); }}
              title={pinned ? "Unpin" : "Pin to the top"} aria-label={pinned ? `Unpin ${r.label}` : `Pin ${r.label}`}
              data-testid={`${testid}-pin`}
              className={`absolute right-2 top-2.5 rounded-md p-1 transition-opacity ${
                pinned ? "text-amber-500 opacity-100" : "text-slate-300 opacity-0 hover:text-amber-500 group-hover:opacity-100 focus:opacity-100"}`}>
        <Star className={`h-3.5 w-3.5 ${pinned ? "fill-current" : ""}`} />
      </button>
    </li>
  );
}

export default function Reports() {
  const { reportKey } = useParams();
  const navigate = useNavigate();
  const { user, has } = useAuth();
  const canCdr = has("billing_sync.view");
  const isSuper = user?.base_role === "super_admin" || user?.role === "super_admin";
  const [sp, setSp] = useSearchParams();
  const tab = TABS.includes(sp.get("tab")) ? sp.get("tab") : "catalog";
  const [catalog, setCatalog] = useState(null);
  const [favs, setFavs] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/report-hub/catalog").then((r) => { setCatalog(r.data); setFavs(r.data.favourites || []); })
      .catch(() => setCatalog({ reports: [], groups: [], all_data: false }));
  }, []);

  const reports = catalog?.reports || [];
  const pinned = favs.map((k) => reports.find((r) => r.key === k)).filter(Boolean);
  const selected = reports.find((r) => r.key === reportKey) || (reportKey ? null : (pinned[0] || reports[0]));

  const toggleFav = async (key) => {
    const next = favs.includes(key) ? favs.filter((k) => k !== key) : [...favs, key];
    setFavs(next);
    try {
      const { data } = await api.put("/report-hub/favourites", { keys: next });
      setFavs(data.favourites || next);
    } catch { setFavs(favs); toast.error("Could not save your pinned reports"); }
  };

  usePageBreadcrumb(useMemo(() => (selected && reportKey
    ? [{ label: "Reports", to: "/reports" }, { label: selected.label }]
    : [{ label: "Reports" }]), [selected, reportKey]));

  const setTab = (t) => { const n = new URLSearchParams(sp); if (t === "catalog") n.delete("tab"); else n.set("tab", t); setSp(n, { replace: true }); };
  const openReport = (key) => { navigate(`/reports/${key}`); };

  return (
    <div data-testid="reports-page">
      <PageHeader
        testid="reports-header"
        title="Reports"
        subtitle="Generate any report on the fly, download it, email it, or put it on a schedule."
        titleAccessory={catalog && (
          <span className={`ml-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            catalog.all_data ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                             : "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}
                data-testid="reports-scope-badge">
            {catalog.all_data ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            <span className="font-sans tracking-normal">{catalog.all_data ? "All data" : "Scoped to your data"}</span>
          </span>
        )}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="catalog" data-testid="reports-tab-catalog"><BarChart3 className="mr-1.5 h-4 w-4" /> Catalog</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="reports-tab-schedules"><CalendarClock className="mr-1.5 h-4 w-4" /> Schedules</TabsTrigger>
          <TabsTrigger value="history" data-testid="reports-tab-history"><History className="mr-1.5 h-4 w-4" /> History</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="reports-tab-notifications"><BellRing className="mr-1.5 h-4 w-4" /> Email notifications</TabsTrigger>
          {canCdr && (
            <TabsTrigger value="cdr-files" data-testid="reports-tab-cdr-files"><PhoneCall className="mr-1.5 h-4 w-4" /> CDR files</TabsTrigger>
          )}
          {isSuper && (
            <TabsTrigger value="ai-spend" data-testid="reports-tab-ai-spend"><Sparkles className="mr-1.5 h-4 w-4" /> AI Spend Guard</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="catalog" className="mt-4">
          {!catalog ? (
            <div className="py-16 text-center text-sm text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          ) : reports.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="reports-none">
              <FileText className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3">None of the reports are open to your role yet — each report follows the permission of the feature it comes from.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
              <nav className="space-y-5" data-testid="reports-catalog">
                {pinned.length > 0 && (
                  <section data-testid="reports-group-pinned">
                    <div className="mb-1.5 flex items-center gap-1 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
                      <Star className="h-3 w-3 fill-current" /> Pinned
                    </div>
                    <ul className="space-y-0.5">
                      {pinned.map((r) => (
                        <CatalogItem key={`pin-${r.key}`} r={r} active={selected?.key === r.key} pinned
                                     onOpen={openReport} onToggleFav={toggleFav} testid={`report-pinned-${r.key}`} />
                      ))}
                    </ul>
                  </section>
                )}
                {catalog.groups.map((g) => (
                  <section key={g} data-testid={`reports-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{g}</div>
                    <ul className="space-y-0.5">
                      {reports.filter((r) => r.group === g).map((r) => (
                        <CatalogItem key={r.key} r={r} active={selected?.key === r.key} pinned={favs.includes(r.key)}
                                     onOpen={openReport} onToggleFav={toggleFav} testid={`report-card-${r.key}`} />
                      ))}
                    </ul>
                  </section>
                ))}
              </nav>
              {selected ? (
                <ReportWorkbench report={selected} allData={catalog.all_data} onScheduled={() => setRefreshKey((k) => k + 1)} />
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="reports-not-found">
                  That report doesn't exist or isn't open to you. Pick one on the left.
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="schedules" className="mt-4">
          <SchedulesTab reports={reports} refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <RunsTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="notifications" className="mt-4">
          <NotificationPrefsTab />
        </TabsContent>
        {canCdr && (
          <TabsContent value="cdr-files" className="mt-4" data-testid="reports-cdr-files-content">
            {tab === "cdr-files" && <CdrFilesTab />}
          </TabsContent>
        )}
        {isSuper && (
          <TabsContent value="ai-spend" className="mt-4" data-testid="reports-ai-spend-content">
            <p className="mb-3 text-xs text-slate-400">Monthly Claude tokens &amp; cost by feature and user — super admins only.</p>
            <Card className="p-4"><AiSpendPanel /></Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
